import React from 'react';
import cx from 'classnames';
import { CommonProps } from '../../types';

import styles from './Spacing.less';

export const spacingOptions = [
  'p',
  'pt',
  'pr',
  'pb',
  'pl',
  'px',
  'py',
  'm',
  'mt',
  'mr',
  'mb',
  'ml',
  'mx',
  'my',
];

type SpacingUnits = true | 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 'auto';

export interface SpacingProps extends CommonProps {
  /**
   * Sets the `padding` property
   */
  p?: SpacingUnits;
  /**
   * Sets the `padding-top` property
   */
  pt?: SpacingUnits;
  /**
   * Sets the `padding-right` property
   */
  pr?: SpacingUnits;
  /**
   * Sets the `padding-bottom` property
   */
  pb?: SpacingUnits;
  /**
   * Sets the `padding-left` property
   */
  pl?: SpacingUnits;
  /**
   * Sets the `padding` property for right and left
   */
  px?: SpacingUnits;
  /**
   * Sets the `padding` property for top and bottom
   */
  py?: SpacingUnits;
  /**
   * Sets the `margin` property
   */
  m?: SpacingUnits;
  /**
   * Sets the `margin-top` property
   */
  mt?: SpacingUnits;
  /**
   * Sets the `margin-right` property
   */
  mr?: SpacingUnits;
  /**
   * Sets the `margin-bottom` property
   */
  mb?: SpacingUnits;
  /**
   * Sets the `margin-left` property
   */
  ml?: SpacingUnits;
  /**
   * Sets the `margin` property for right and left
   */
  mx?: SpacingUnits;
  /**
   * Sets the `margin` property for top and bottom
   */
  my?: SpacingUnits;
  /**
   * The HTML element or React element used to render with spacing, e.g. 'div', 'a', or 'span'.
   * @default 'div'
   * @TODO: enable any JSX element to be used
   */
  asComponent?: 'div' | 'a' | 'span';
}

const filterSpacingProps = (props: SpacingProps) =>
  Object.keys(props)
    .filter((key) => !spacingOptions.includes(key))
    .reduce(
      (pluckProps, key) => ({
        ...pluckProps,
        [key]: (props as Record<string, string | number>)[key],
      }),
      {},
    );

export const useSpacing = (props: SpacingProps): string => {
  const spacingClassObj = spacingOptions.reduce((obj, spacing) => {
    let propSpacing = (props as Record<string, string | number>)[spacing];

    // put the class if prop = 'auto' or a number 0 - 8,
    let shouldPutClass =
      propSpacing === 'auto' || (propSpacing >= 0 && propSpacing <= 8);

    // or if prop is provided without value, set default to 4 (8px)
    if (propSpacing && propSpacing.toString() === 'true') {
      shouldPutClass = true;
      propSpacing = 4;
    }
    return {
      ...obj,
      ...(shouldPutClass && {
        [styles[`${spacing}-${propSpacing}`]]: shouldPutClass,
      }),
    };
  }, {});

  return Object.keys(spacingClassObj).join(' ');
};

export const Spacing = (props: SpacingProps) => {
  const { className, asComponent: Component = 'div', ...rest } = props;
  const filteredProps = filterSpacingProps(rest);
  const spacingCx = useSpacing(props);

  return <Component className={cx(spacingCx, className)} {...filteredProps} />;
};
